import React, {
  ComponentProps,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Platform,
} from 'react-native';

import { Box } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import useGlobalSearch from '@hooks/useGlobalSearch';
import theme from '@themes/theme';

type SearchInputProps = {
  onTextChanged: (searchText: string) => void;
  value?: string;
  placeholder?: string;
  onTabOut?: () => void;
  onCancel?: () => void;
  isTagSearch?: boolean;
  showBackButton?: boolean;
  showOverlay?: boolean;
  loading?: boolean;
} & Pick<
  ComponentProps<typeof TextInput>,
  'onFocus' | 'onBlur' | 'autoFocus' | 'editable'
>;

export const SearchInputV3 = React.forwardRef<TextInput, SearchInputProps>(
  (
    {
      onTextChanged,
      autoFocus,
      onFocus,
      onBlur,
      onTabOut,
      value,
      editable,
      isTagSearch,
      onCancel,
      showBackButton = true,
      showOverlay = false,
      placeholder,
      loading = false,
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);
    const [internalValue, setInternalValue] = useState('');
    const { isViewAllPress, setViewAllEvent } = useGlobalSearch();
    const PLACEHOLDER = placeholder ?? (isTagSearch ? 'Search Tag' : 'Search');
    const ref_inputBox = useRef<TextInput>(null);
    useImperativeHandle(ref, () => ref_inputBox.current as TextInput);

    useEffect(() => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      if (autoFocus === true) {
        timeoutId = setTimeout(() => {
          ref_inputBox?.current?.focus();
        }, 600);
      }

      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }, [autoFocus]);
    return (
      <Box
        flex={1}
        backgroundColor={Platform.OS === 'web' ? 'grey01' : 'white'}
        borderRadius='xs'
        borderWidth={Platform.OS === 'web' ? 0 : 1}
        borderColor='grey02'
        height={Platform.OS === 'web' ? 40 : 48}
        flexDirection='row'
        alignItems='center'>
        {showBackButton && !isViewAllPress && (
          <Icon
            name='ChevronLeft'
            variant='xl'
            onPress={() => setViewAllEvent(true)}
          />
        )}
        <Box
          flex={1}
          backgroundColor={
            showOverlay && Platform.OS !== 'web' ? 'white' : 'grey01'
          }
          borderRadius='xs'
          flexDirection='row'
          alignItems='center'
          paddingVertical='xs'
          paddingHorizontal='m'
          accessibilityLabel='Search'
          style={{ zIndex: showOverlay ? 99999 : undefined }}>
          <Icon
            name={isTagSearch ? 'HashSearch' : 'Search'}
            variant='l'
            color='textPrimary'
          />
          <TextInput
            editable={editable}
            ref={ref_inputBox}
            placeholderTextColor={theme.colors.grey04}
            selectionColor={theme.colors.textPrimary}
            cursorColor={theme.colors.textPrimary}
            style={[
              styles.input,
              Platform.OS === 'web'
                ? theme.textVariants.labelSmall
                : theme.textVariants.webBodySecondary,
              // ✅ Android fix
              {
                paddingVertical: 0,
                textAlignVertical: 'center',
                color: theme.colors.textPrimary,
                ...(Platform.OS === 'android'
                  ? { includeFontPadding: false }
                  : null),
              },
            ]}
            placeholder={PLACEHOLDER}
            value={value}
            autoCapitalize='none'
            autoFocus={undefined}
            onKeyPress={
              onTabOut &&
              (({ nativeEvent: { key } }) =>
                key === 'Tab' && focused && onTabOut())
            }
            onBlur={(e) => {
              onBlur?.(e);
              setFocused(false);
            }}
            onFocus={(e) => {
              onFocus?.(e);
              setFocused(true);
            }}
            onChangeText={(str) => {
              onTextChanged(str);
            }}
          />
          <TouchableOpacity
            accessibilityLabel={
              loading || (!internalValue && !value) ? undefined : 'Cancel'
            }
            accessible={!loading && (!!internalValue || !!value)}
            importantForAccessibility={
              loading || (!internalValue && !value) ? 'no' : 'yes'
            }
            disabled={loading || (!internalValue && !value)}
            onPress={() => {
              ref_inputBox?.current?.focus();
              setFocused(true);
              onTextChanged('');
              setInternalValue('');
              if (onCancel) {
                setViewAllEvent(false);
                onCancel();
              }
            }}>
            {(!!internalValue || !!value) &&
              (loading ? (
                <ActivityIndicator
                  size='small'
                  color={theme.colors.textPrimary}
                />
              ) : (
                <Icon name='X' variant='m' color='textPrimary' />
              ))}
          </TouchableOpacity>
        </Box>
      </Box>
    );
  }
);
const styles = StyleSheet.create({
  input: {
    marginLeft: theme.spacing.xs,
    flex: 1,
    minHeight: 30,
    color: theme.colors.textPrimary,
    ...(Platform.OS === 'web'
      ? {
          outlineStyle: 'none',
          outlineWidth: 0,
          boxShadow: 'none',
        }
      : null),
  },
});
